import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Check, Sparkles, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabaseClient';
import { safeFetch } from '@/lib/safeFetch';
import { useToast } from '@/hooks/use-toast';

import { samplePlans } from '@/data/samplePlans';

interface Plan {
  id: string;
  name: string;
  duration: string;
  price: number;
  original_price?: number;
  originalPrice?: number; // compat
  description: string;
  features: string[];
  popular: boolean | string;
  buttonText?: string;
  plan_key?: string;
}

interface PricingSectionProps {
  courseId?: string;
}

const getPlanFeatures = (name: string) => {
  const lowerName = name.toLowerCase();
  if (lowerName.includes('starter')) {
    return [
      '1 Month Access',
      'Basic SMC Introduction',
      'Chart Mastery Basics',
      'Live Support'
    ];
  }
  if (lowerName.includes('professional')) {
    return [
      '3 Months Access',
      'Advanced SMC Strategies',
      'Weekly Q&A Sessions',
      'Premium Discord Access'
    ];
  }
  if (lowerName.includes('premium')) {
    return [
      '6 Months Access',
      'Personalized Mentorship',
      'The Private Strategy',
      'Life-Time Community Sync'
    ];
  }
  return [];
};

const PricingSection = ({ courseId }: PricingSectionProps) => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRedirecting, setIsRedirecting] = useState<string | null>(null);
  const [userEnrollments, setUserEnrollments] = useState<string[]>([]);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [targetCourseId, setTargetCourseId] = useState<string | null>(null);

  const handleStartLearning = async (pCourseId: string) => {
    if (!user) {
      navigate('/login');
      return;
    }

    setIsRedirecting(pCourseId);
    try {
      console.log("--- START LEARNING TELEMETRY: INITIATING FLOW ---");

      // 1. Ensure Profile Exists (Fail-safe for FK constraints)
      const { data: profile } = await supabase
        .from('profiles')
        .select('id')
        .eq('id', user.id)
        .maybeSingle();

      if (!profile) {
        console.log("--- PROFILE PROTOCOL: AUTO-PROVISIONING ---");
        await supabase.from('profiles').insert({
          id: user.id,
          email: user.email.toLowerCase(),
          full_name: user.full_name || 'User',
          role: 'student',
          status: 'active'
        });
      }

      // 2. Fetch enrollment status (Safety Check)
      const { data: enrollment, error: enrollCheckError } = await supabase
        .from('enrollments')
        .select('id')
        .eq('user_id', user.id)
        .eq('course_id', pCourseId)
        .maybeSingle();

      if (enrollCheckError || !enrollment) {
        console.error("Enrollment Validation Failed:", enrollCheckError);
        toast({
          title: "Access Verification Failed",
          description: "You do not have an active enrollment for this course. Please purchase a plan.",
          variant: "destructive"
        });
        return;
      }

      // 3. Set standard success flag and redirect
      localStorage.setItem('payment_status', 'success');
      console.log("Redirecting to Completion Celebration (Mastery Unlocked).");
      navigate(`/payment-success?courseId=${pCourseId}`);
    } catch (err) {
      console.error("Critical Sequence Disruption:", err);
    } finally {
      setIsRedirecting(null);
    }
  };

  useEffect(() => {
    const fetchPlansAndEnrollments = async () => {
      setIsLoading(true);
      let targetCourseId = courseId;

      // Find the most recent active course if none provided
      if (!targetCourseId) {
        const { data: courseData } = await supabase
          .from('courses')
          .select('id')
          .eq('status', 'active')
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (courseData) {
          targetCourseId = courseData.id;
          setTargetCourseId(courseData.id);
          console.log("--- PRICING TELEMETRY: ACTIVE COURSE LOCALIZED ---", courseData.id);
        } else {
          console.warn("--- PRICING TELEMETRY: NO ACTIVE COURSES IN DOMAIN ---");
        }
      } else {
        setTargetCourseId(courseId);
      }

      // Fetch user enrollments if logged in
      if (user) {
        const { data: enrollData } = await supabase
          .from('enrollments')
          .select('course_id')
          .eq('user_id', user.id);

        if (enrollData) {
          setUserEnrollments(enrollData.map(e => e.course_id));
        }
      }

      if (targetCourseId) {
        const data = await safeFetch<Plan[]>(
          supabase.from('plans').select('*').eq('course_id', targetCourseId).order('price', { ascending: true }),
          [],
          'Fetching course pricing tiers'
        );

        if (data && data.length > 0) {
          setPlans(data);
        } else {
          setPlans(samplePlans as any);
        }
      } else {
        setPlans(samplePlans as any);
      }
      setIsLoading(false);
    };

    fetchPlansAndEnrollments();
  }, [courseId, user]);

  return (
    <section className="section-padding bg-background relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-accent/3 blur-[120px] pointer-events-none" />

      <div className="container-max relative z-10">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-20">
          <span className="inline-block px-4 py-2 bg-accent/5 border border-accent/20 rounded-full text-accent-foreground text-[10px] font-black uppercase tracking-[0.2em] mb-6">
            Elite Access
          </span>
          <h2 className="heading-lg text-primary mb-6">
            Select Your <span className="text-gradient-gold">Mastery Path</span>
          </h2>
          <p className="text-lg text-muted-foreground font-medium">
            Strategic investment in your education. Professional-grade trading wisdom for those committed to financial freedom.
          </p>
        </div>

        {/* Plans Grid */}
        <div className="grid md:grid-cols-3 gap-8 items-stretch max-w-6xl mx-auto">
          {isLoading ? (
            <div className="col-span-3 py-20 text-center">
              <div className="inline-block animate-spin w-10 h-10 border-4 border-accent border-t-transparent rounded-full mb-4"></div>
              <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Synchronizing Enrollment Protocols...</p>
            </div>
          ) : (
            plans.map((plan) => {
              const cid = (plan as any).course_id || courseId || targetCourseId;
              const isProfessional = plan.name.toLowerCase().includes('professional');
              const isPopular = plan.popular === true || plan.popular === 'true';
              const staticFeatures = getPlanFeatures(plan.name);

              return (
                <div
                  key={plan.id}
                  className={`relative flex flex-col h-full bg-white transition-all duration-500 rounded-[2.5rem] p-10 shadow-sm ${isProfessional
                    ? 'border-2 border-[#D4AF37] ring-8 ring-[#D4AF37]/5 scale-[1.02] z-10 shadow-[0_20px_50px_rgba(212,175,55,0.15)]'
                    : 'border border-slate-100 hover:shadow-xl hover:scale-[1.01]'
                    }`}
                >
                  {(isProfessional || isPopular) && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-5 py-1.5 bg-gradient-to-r from-[#D4AF37] to-[#B8860B] rounded-full text-white text-[9px] font-black uppercase tracking-widest shadow-lg flex items-center gap-2 whitespace-nowrap">
                      <Sparkles className="w-3 h-3" />
                      Highly Recommended
                    </div>
                  )}

                  <div className="flex flex-col h-full">
                    <div className="mb-8">
                      <h3 className="text-2xl font-black text-slate-900 uppercase tracking-tight mb-1">{plan.name}</h3>
                      <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">{plan.duration}</p>
                    </div>

                    <div className="mb-8 p-8 bg-slate-50 rounded-[2rem] border border-slate-100 text-center flex flex-col justify-center items-center">
                      {(plan.original_price || plan.originalPrice) && (
                        <span className="text-sm font-bold text-slate-400 line-through mb-1 uppercase tracking-tighter">
                          ₹{(plan.original_price || plan.originalPrice || 0).toLocaleString()}
                        </span>
                      )}
                      <div className="flex items-baseline justify-center gap-1">
                        <span className="text-5xl font-black text-slate-900 tracking-tighter italic">₹{plan.price.toLocaleString()}</span>
                      </div>
                    </div>

                    <div className="space-y-6 mb-10 flex-1">
                      {((plan.features && plan.features.length > 0) ? plan.features : staticFeatures).map((feature, i) => (
                        <div key={i} className="flex items-start gap-4">
                          <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${isProfessional ? 'bg-[#D4AF37]/10 text-[#D4AF37]' : 'bg-slate-100 text-slate-400'
                            }`}>
                            <Check className="w-3 h-3 stroke-[3px]" />
                          </div>
                          <span className="text-sm font-bold text-slate-600 leading-snug">{feature}</span>
                        </div>
                      ))}
                    </div>

                    <div className="mt-auto">
                      {(cid && userEnrollments.includes(cid as string)) ? (
                        <Button
                          type="button"
                          onClick={async () => {
                            console.log("--- START LEARNING ATTEMPT: CID RESOLUTION ---", cid);
                            if (cid) {
                              await handleStartLearning(cid);
                            } else {
                              toast({
                                title: "Course identification failed",
                                description: "The active terminal could not be localized. Please refresh.",
                                variant: "destructive"
                              });
                            }
                          }}
                          disabled={isRedirecting !== null}
                          className="w-full h-14 rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] shadow-lg transition-all duration-300 hover:-translate-y-0.5 active:translate-y-0 bg-emerald-600 hover:bg-emerald-700 text-white border-0"
                        >
                          <div className="flex items-center gap-2">
                            <Zap className="w-3 h-3 text-white" />
                            {isRedirecting && isRedirecting === cid ? 'Synchronizing Archive...' : 'Start Learning'}
                          </div>
                        </Button>
                      ) : (
                        <Link to={`/checkout/${plan.plan_key || plan.id}`}>
                          <Button
                            className={`w-full h-14 rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] shadow-lg transition-all duration-300 hover:-translate-y-0.5 active:translate-y-0 ${isProfessional
                              ? 'bg-gradient-to-r from-[#D4AF37] to-[#B8860B] hover:shadow-[#D4AF37]/30 text-white border-0'
                              : 'bg-[#001B3D] hover:bg-[#002654] text-white border-0'
                              }`}
                          >
                            {plan.buttonText || "Enroll Now"}
                          </Button>
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>


        {/* Trust Badge */}
        <div className="text-center mt-20 p-8 border-t border-gray-100">
          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground/60 flex items-center justify-center gap-4">
            <span className="w-12 h-px bg-gray-200" />
            Secure Enrollment Protocol Active
            <span className="w-12 h-px bg-gray-200" />
          </p>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
